import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import { Layout } from "./components/Layout";
import { pingBackend } from "./lib/warmup";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Fixtures from "./pages/Fixtures";
import MyPredictions from "./pages/MyPredictions";
import Leaderboard from "./pages/Leaderboard";
import Admin from "./pages/Admin";
import Leagues from "./pages/Leagues";
import LeagueDetail from "./pages/LeagueDetail";
import Champions from "./pages/Champions";
import Profile from "./pages/Profile";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Stats from "./pages/Stats";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";
import GetStarted from "./pages/GetStarted";
import AuthCallback from "./pages/AuthCallback";
import Unsubscribe from "./pages/Unsubscribe";

const TOASTER_OPTIONS = {
  style: {
    background: "#121418",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#fff",
    borderRadius: 0,
  },
};

const Protected = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-white/60 uppercase tracking-widest">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/" replace />;
  return children;
};

function App() {
  // Fire a lightweight ping on app mount to warm the backend pod before the
  // user reaches Login/Register — avoids the visible cold-start pause.
  useEffect(() => {
    pingBackend();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Layout>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/fixtures" element={<Protected><Fixtures /></Protected>} />
            <Route path="/predictions" element={<Protected><MyPredictions /></Protected>} />
            <Route path="/stats" element={<Protected><Stats /></Protected>} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/champions" element={<Champions />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment-success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/leagues" element={<Protected><Leagues /></Protected>} />
            <Route path="/leagues/:id" element={<Protected><LeagueDetail /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
        <Toaster theme="dark" position="top-right" toastOptions={TOASTER_OPTIONS} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
